import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, TextField } from "@mui/material";
import { ChangeEvent, useState, FocusEvent } from "react";
import { DocumentIntake, DocumentIntakeViewBasic } from "generated/graphql";
import useAppDispatchWithSnackbar from "helpers/hooks/useAppDispatchWithSnackbar";
import { validateNewFileName } from "Backend";
import { renameIntake } from "redux/slices/case";

export interface RenameIntakeProps {
  open: boolean;
  close: () => void;
  intake: DocumentIntake | DocumentIntakeViewBasic;
}

const RenameIntakeDialog = (props: RenameIntakeProps) => {
  const { open, close, intake } = props;
  const [newName, setNewName] = useState<string>(intake.name);
  const [nameError, setNameError] = useState<boolean>(false);
  const [nameHelperText, setNameHelperText] = useState<string>("");
  const [renaming, setRenaming] = useState(false);
  const snackpatch = useAppDispatchWithSnackbar();
  const rename = async (intakeId: number, newName: string) => {
    setRenaming(true);
    snackpatch(renameIntake({ intakeId, newName }), {
      successMessage: "Document renamed successfully",
      errorMessage: "Unable to rename document",
    })
      .then(() => {
        close();
      })
      .finally(() => {
        setRenaming(false);
      });
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    setNewName(event.target.value);
    const validationStatus = validateNewFileName(intake.name, event.target.value);
    if (validationStatus) {
      setNameError(true);
      setNameHelperText(validationStatus);
    } else {
      setNameError(false);
      setNameHelperText("");
    }
  };

  const handleFocus = (event: FocusEvent<any>) => {
    event.preventDefault();
    const { target } = event;
    const extensionStarts = target.value.lastIndexOf(".");
    target.focus();
    target.setSelectionRange(0, extensionStarts);
  };

  return (
    <Dialog open={open} onClose={close} maxWidth="sm" fullWidth>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          rename(intake.id, newName);
        }}>
        <DialogTitle>Rename Document</DialogTitle>
        <DialogContent>
          <DialogContentText>Please enter the new name for this document</DialogContentText>
          <TextField
            sx={{ mt: 2 }}
            defaultValue={newName}
            autoFocus
            onFocus={handleFocus}
            id="name"
            label="Document Name"
            type="text"
            fullWidth
            onChange={handleChange}
            error={nameError}
            helperText={nameHelperText}
          />
        </DialogContent>
        <DialogActions>
          <Button color="default" onClick={close}>
            Cancel
          </Button>
          <Button variant="contained" color="default" type="submit" disabled={renaming || nameError}>
            {renaming ? "Saving" : "Save"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default RenameIntakeDialog;
